import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building, Check, ChevronLeft, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function FacilityApplicationForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    facility_name: '',
    facility_type: 'sports_complex',
    address: '',
    city: '',
    country: '',
    available_sports: [],
    description: '',
    photos: [],
    documents: [],
    pricing_info: {
      hourly_rate: '',
      daily_rate: '',
      weekly_rate: '',
      monthly_rate: ''
    }
  });
  
  const [newSport, setNewSport] = useState('');
  const [newPhoto, setNewPhoto] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Convert pricing to numbers
    const submissionData = {
      ...formData,
      pricing_info: {
        hourly_rate: parseFloat(formData.pricing_info.hourly_rate) || 0,
        daily_rate: parseFloat(formData.pricing_info.daily_rate) || 0,
        weekly_rate: parseFloat(formData.pricing_info.weekly_rate) || 0,
        monthly_rate: parseFloat(formData.pricing_info.monthly_rate) || 0
      }
    };
    
    try {
      const response = await axios.post(`${API}/synergies/facilities/apply`, submissionData);
      setSubmitted(true);
      console.log('Application submitted:', response.data);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const addSport = () => {
    if (newSport.trim()) {
      setFormData({
        ...formData,
        available_sports: [...formData.available_sports, newSport.trim().toLowerCase()]
      });
      setNewSport('');
    }
  };

  const removeSport = (index) => {
    setFormData({
      ...formData,
      available_sports: formData.available_sports.filter((_, i) => i !== index)
    });
  };

  const addPhoto = () => {
    if (newPhoto.trim()) {
      setFormData({
        ...formData,
        photos: [...formData.photos, newPhoto.trim()]
      });
      setNewPhoto('');
    }
  };

  const removePhoto = (index) => {
    setFormData({
      ...formData,
      photos: formData.photos.filter((_, i) => i !== index)
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center px-4">
        <div className="bg-dark-surface rounded-xl p-8 border border-premium-gold/50 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-premium-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-premium-gold" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Application Submitted!
          </h2>
          <p className="text-white/70 mb-6">
            Thank you for your interest in joining DIOS Synergies as a facility partner! 
            We'll review your application and get back to you within 2-3 business days.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-premium-gold hover:bg-red-700 text-white font-bold tracking-wider py-3 rounded-lg transition-all duration-300"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-dark py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="bg-dark-surface rounded-xl p-8 border border-premium-gold/30/50 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-premium-gold/20 rounded-full flex items-center justify-center">
              <Building className="w-8 h-8 text-premium-gold" />
            </div>
            <div>
              <h1 
                className="text-3xl font-bold text-white uppercase"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                List Your <span className="gold-gradient">Facility</span>
              </h1>
              <p className="text-white/60">Join our global network of sports facilities</p>
            </div>
          </div>
          
          <div className="bg-deep-dark rounded-lg p-4 mb-6">
            <h3 className="text-white font-semibold mb-2">Why Partner with DIOS Synergies?</h3>
            <ul className="space-y-2 text-white/70 text-sm">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Reach global audience of sports enthusiasts and teams</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Increase facility utilization and revenue</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Automated booking and payment management</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Verified listing badge and marketing support</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="bg-dark-surface rounded-xl p-8 border border-premium-gold/30/50 space-y-6">
          <h2 className="text-2xl font-bold text-white mb-6 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
            Facility Application
          </h2>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white/90 border-b border-premium-gold/30/50 pb-2">
              Contact Information
            </h3>
            
            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Contact Person Name *</label>
              <input
                type="text"
                value={formData.applicant_name}
                onChange={(e) => setFormData({ ...formData, applicant_name: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.applicant_email}
                  onChange={(e) => setFormData({ ...formData, applicant_email: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="facility@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={formData.applicant_phone}
                  onChange={(e) => setFormData({ ...formData, applicant_phone: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="+1 234 567 8900"
                  required
                />
              </div>
            </div>
          </div>

          {/* Facility Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white/90 border-b border-premium-gold/30/50 pb-2">
              Facility Information
            </h3>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Facility Name *</label>
              <input
                type="text"
                value={formData.facility_name}
                onChange={(e) => setFormData({ ...formData, facility_name: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                placeholder="e.g. Mumbai Sports Academy"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Facility Type *</label>
              <select
                value={formData.facility_type}
                onChange={(e) => setFormData({ ...formData, facility_type: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base focus:border-premium-gold focus:outline-none"
                required
              >
                <option value="sports_complex">Sports Complex</option>
                <option value="stadium">Stadium</option>
                <option value="gymnasium">Gymnasium</option>
                <option value="swimming_pool">Swimming Pool</option>
                <option value="tennis_court">Tennis Court</option>
                <option value="cricket_ground">Cricket Ground</option>
                <option value="football_field">Football Field</option>
                <option value="basketball_court">Basketball Court</option>
                <option value="badminton_court">Badminton Court</option>
                <option value="fitness_center">Fitness Center</option>
                <option value="turf_ground">Turf Ground</option>
                <option value="athletic_track">Athletic Track</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Address *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                placeholder="Full address with street, area, postal code"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. Mumbai, London"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Country *</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. India, UK"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                rows="4"
                placeholder="Describe your facility, amenities, unique features, etc."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Available Sports *</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newSport}
                  onChange={(e) => setNewSport(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSport())}
                  className="flex-1 px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. cricket, football, tennis"
                />
                <button
                  type="button"
                  onClick={addSport}
                  className="px-4 py-3 bg-premium-gold hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.available_sports.map((sport, idx) => (
                  <span key={idx} className="px-3 py-1 bg-deep-dark text-white rounded-lg flex items-center gap-2 capitalize">
                    {sport}
                    <button
                      type="button"
                      onClick={() => removeSport(idx)}
                      className="text-premium-gold hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
              {formData.available_sports.length === 0 && (
                <p className="text-xs text-white/50 mt-2">Add at least one sport</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Photo URLs (Optional)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="url"
                  value={newPhoto}
                  onChange={(e) => setNewPhoto(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPhoto())}
                  className="flex-1 px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="https://example.com/facility-photo.jpg"
                />
                <button
                  type="button"
                  onClick={addPhoto}
                  className="px-4 py-3 bg-premium-gold hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                {formData.photos.map((photo, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-deep-dark px-3 py-2 rounded-lg">
                    <span className="flex-1 text-sm text-white/70 truncate">{photo}</span>
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="text-premium-gold hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white/90 border-b border-premium-gold/30/50 pb-2">
              Pricing Information (₹)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Hourly Rate *</label>
                <input
                  type="number"
                  min="0"
                  value={formData.pricing_info.hourly_rate}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    pricing_info: { ...formData.pricing_info, hourly_rate: e.target.value }
                  })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. 2000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Daily Rate *</label>
                <input
                  type="number"
                  min="0"
                  value={formData.pricing_info.daily_rate}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    pricing_info: { ...formData.pricing_info, daily_rate: e.target.value }
                  })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. 10000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Weekly Rate (Optional)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.pricing_info.weekly_rate}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    pricing_info: { ...formData.pricing_info, weekly_rate: e.target.value }
                  })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. 50000"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Monthly Rate (Optional)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.pricing_info.monthly_rate}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    pricing_info: { ...formData.pricing_info, monthly_rate: e.target.value }
                  })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. 180000"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-6 border-t border-premium-gold/30/50">
            <button
              type="submit"
              disabled={loading || formData.available_sports.length === 0}
              className="w-full bg-premium-gold hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold tracking-wider py-4 min-h-[48px] rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <Building className="w-5 h-5" />
                </>
              )}
            </button>
            <p className="text-xs text-white/50 text-center mt-4">
              By submitting this application, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FacilityApplicationForm;
